// (Copyright) Confluent, Inc.

import React, { Component, type ReactNode, type HTMLProps } from 'react';
import styled, { css, type DefaultTheme, type CSSProp, useTheme } from 'styled-components';

import { type Color } from '../../constants/colors';
import { analytics } from '../../../utils/analytics';
import { useAnalyticsContext } from '../../../hooks/useAnalyticsContext';
import { legacyStrongTextStyles } from '../StyleGuide';
import { type IconName } from '../icons';
import Icon from '../Icon';
import type { EventProperties } from '../../../utils/events';
import Tooltip from '../Tooltip';
import { cssColor } from '../../utils/color';

import {
  useButtonSubmittingIndicator,
  finishBehaviors,
  type FinishBehavior,
} from './useButtonSubmittingIndicator';

export type ButtonClassType = 'PRIMARY' | 'SECONDARY';
export type ButtonSize = 'LARGE' | 'MEDIUM' | 'SMALL';
export type ButtonPosition = 'FIRST' | 'DEFAULT';

// You can opt a "submitting" button into the final checkmark animation segment with an additional
// prop: `submitFinishBehavior="SHOW_CHECKMARK"`. If you want to avoid showing the button's text
// after the animation is complete (e.g. the final step of a provisioning flow), you should instead
// use `submitFinishBehavior="SHOW_AND_HOLD_CHECKMARK"`.
//
// Youc an explore these different behaviors with the Button Storybook page.
export type ButtonSubmitFinishBehavior = FinishBehavior;

// TODO: Remove more of these and use HTMLProps<HTMLButtonElement> instead
export type ExternalButtonProps = {
  ['aria-label']?: string;
  ['aria-live']?: 'assertive' | 'off' | 'polite';
  ['data-test']?: string;
  ['data-testid']?: string;
  buttonRef?: any;
  css?: CSSProp;
  disabled?: boolean;
  icon?: IconName;
  iconOrder?: 'last' | 'first';
  iconSize?: number;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onSubmitAnimationFinished?: () => void | null | undefined;
  open?: boolean;
  placeBehind?: boolean;
  position?: ButtonPosition;
  skipTracking?: boolean;
  submitFinishBehavior?: FinishBehavior;
  submitting?: boolean;
  tabIndex?: number;
  text?: string;
  tooltip?: ReactNode;
  type?: 'button' | 'reset' | 'submit';
};

type ButtonProps = {
  ['data-test']?: string;
} & ExternalButtonProps & {
    className?: string;
    classType?: ButtonClassType;
    size?: ButtonSize;
  };

type TextProps = {
  children: ReactNode;
  classType?: ButtonClassType;
  disabled?: boolean;
  isSubmittingIndicatorActive?: boolean;
  size?: ButtonSize;
};

type StyledIconProps = {
  ariaHidden: boolean;
  classType: ButtonClassType;
  disabled: boolean;
  fillColor?: string;
  isIconButton: boolean;
  name: IconName;
  size: number;
};

type ButtonClickProps = {
  analyticsProperties: EventProperties;
  disabled?: boolean;
  icon?: string;
  onClick?: React.MouseEventHandler;
  skipTracking?: boolean;
  text?: string;
  type: 'button' | 'link';
};

export const buttonSubmitFinishBehaviors = finishBehaviors;

export const Text = styled.div<TextProps>`
  white-space: nowrap;
  ${legacyStrongTextStyles}

  color: ${({ classType, disabled, theme }) =>
    classType === 'PRIMARY'
      ? theme.colors.text_on_color
      : disabled
      ? theme.colors.text_on_color_disabled
      : theme.colors.button_secondary};

  ${({ classType, disabled, theme }) =>
    classType === 'SECONDARY' && !disabled
      ? css`
          ${StyledButton}:focus &&,
          ${StyledButton}:hover && {
            color: ${theme.colors.button_secondary_hover};
          }
        `
      : ''}
`;

const getIconColor = ({
  classType,
  fillColor,
  disabled,
  theme,
}: StyledIconProps & {
  theme: DefaultTheme;
}) =>
  fillColor
    ? fillColor
    : classType === 'PRIMARY'
    ? theme.colors.icon_on_color
    : disabled
    ? theme.colors.text_on_color_disabled
    : theme.colors.button_secondary;

export const StyledIcon = styled(Icon)<StyledIconProps>`
  ${({ isIconButton, iconOrder }) =>
    !isIconButton &&
    (iconOrder === 'last'
      ? css`
          margin-left: 5px;
          margin-top: 2px;
        `
      : css`
          margin-right: 5px;
          margin-top: 2px;
        `)}

  path,
  ellipse,
  circle,
  rect {
    fill: ${(props) => getIconColor(props)};
  }

  ellipse {
    stroke: ${(props) => getIconColor(props)};
  }

  ${({ classType, disabled, theme }) =>
    classType === 'SECONDARY' && !disabled
      ? css`
          ${StyledButton}:focus &&,
          ${StyledButton}:hover && {
            path,
            ellipse,
            circle,
            rect {
              fill: ${theme.colors.button_secondary_hover};
            }

            ellipse {
              stroke: ${theme.colors.button_secondary_hover};
            }
          }
        `
      : ''}
`;

export const ButtonChildrenWrapper = styled.div<{
  placeBehind?: boolean;
  isSubmittingIndicatorActive?: boolean;
  hasIconAndText: boolean;
  reverse?: boolean;
}>`
  position: relative;
  z-index: ${({ placeBehind }) => (placeBehind ? '0' : '1')};

  transition: opacity 150ms ease-in;
  opacity: ${({ isSubmittingIndicatorActive }) => (isSubmittingIndicatorActive ? 0 : 1)};

  ${({ hasIconAndText }) =>
    hasIconAndText &&
    css<{ reverse?: boolean }>`
      display: flex;
      flex-direction: ${(props) => (props.reverse ? 'row-reverse' : 'row')};
      align-items: center;
      justify-content: center;
    `}
`;

type ButtonBorderProps = {
  ['aria-disabled']?: boolean | 'false' | 'true';
  classType: ButtonClassType;
  disabled?: boolean;
  isSubmittingIndicatorActive: boolean;
};

const handleButtonClick = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLDivElement, MouseEvent>,
  props: ButtonClickProps
) => {
  if (props.disabled) {
    e.preventDefault();
    return;
  }

  const { onClick, icon, text: textProp, skipTracking = false, type, analyticsProperties } = props;

  if (typeof onClick === 'function') {
    onClick(e);
  }

  // naming this url so that it's consistent w match -- https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/match.md
  const url = window.location.pathname;
  const text = textProp || '';
  if (!skipTracking) {
    analytics.track({
      name: 'Base Button clicked',
      properties: {
        ...analyticsProperties,
        source: analyticsProperties.source ?? [],
        icon,
        url,
        text,
        type,
      },
    });
  }
};

const Btn = ({ disabled, size = 'MEDIUM', type = 'button', ...props }: ButtonProps) => {
  const {
    buttonRef: propRef,
    icon,
    iconOrder,
    text,
    classType = 'PRIMARY',
    onClick,
    skipTracking,
    submitting,
    tooltip,
    iconSize: _iconSize,
    onSubmitAnimationFinished,
    submitFinishBehavior,
  } = props;
  const innerRef = React.useRef();
  const buttonRef = propRef || innerRef;
  const iconSize = _iconSize != null ? _iconSize : size === 'LARGE' ? 20 : 16;
  const { getProperties } = useAnalyticsContext();
  const { indicator: submittingIndicator, isActive: isSubmittingIndicatorActive } =
    useButtonSubmittingIndicator({
      buttonClassType: classType,
      buttonSize: size,
      submitting: Boolean(submitting),
      onAnimationFinished: onSubmitAnimationFinished,
      finishBehavior: submitFinishBehavior,
    });

  const isDisabled = disabled === true || isSubmittingIndicatorActive;

  const Comp = (
    <StyledButton
      aria-disabled={isDisabled}
      type={type}
      {...props}
      $size={size}
      classType={classType}
      isSubmittingIndicatorActive={isSubmittingIndicatorActive}
      onClick={(e) =>
        handleButtonClick(e, {
          onClick,
          icon,
          text,
          skipTracking,
          type: 'button',
          disabled: isDisabled,
          analyticsProperties: getProperties(),
        })
      }
      ref={buttonRef}
      text={text}
    >
      <ButtonChildrenWrapper
        hasIconAndText={typeof icon === 'string' && typeof text === 'string' && text.length > 0}
        isSubmittingIndicatorActive={isSubmittingIndicatorActive}
        placeBehind={props.placeBehind}
        reverse={iconOrder === 'last'}
      >
        {typeof icon === 'string' && (
          <StyledIcon
            ariaHidden={Boolean(text)} // if there is text, hide the icon from screen reader
            classType={classType}
            disabled={disabled}
            iconOrder={iconOrder}
            isIconButton={!text}
            name={icon}
            size={iconSize}
          />
        )}
        {typeof text === 'string' && text.length > 0 && (
          <Text classType={classType} disabled={disabled === true} size={size}>
            {text}
          </Text>
        )}
      </ButtonChildrenWrapper>
      {submittingIndicator}
    </StyledButton>
  );

  // Filter out all falsy values except 0
  if (tooltip || tooltip === 0) {
    return (
      <Tooltip content={tooltip} ref={buttonRef}>
        {Comp}
      </Tooltip>
    );
  }

  return Comp;
};

/**
 * @deprecated Use `Button` from `core/modules/components` instead.
 */
export const Button = styled(Btn)`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  font-weight: 500;
  border: none;
`;

type StyledButtonProps = HTMLProps<HTMLButtonElement> & {
  children;
  classType: ButtonClassType;
  disabled?: boolean;
  isSubmittingIndicatorActive: boolean;
  position?: ButtonPosition;
  $size?: ButtonSize;
  text?: string;
};

const getBorderStyle = ({
  'aria-disabled': disabled,
  classType,
  isSubmittingIndicatorActive,
  theme,
}: ButtonBorderProps & {
  theme: DefaultTheme;
}) => {
  switch (classType) {
    case 'PRIMARY':
      // Primary buttons never have a border
      return '';
    case 'SECONDARY':
      if (disabled && !isSubmittingIndicatorActive) {
        // Disabled secondary border
        return css`
          box-shadow: inset 0 0 0 1px ${theme.colors.button_disabled};
        `;
      }

      // Normal or submitting secondary border
      return css`
        box-shadow: inset 0 0 0 1px ${theme.colors.button_secondary};
      `;
  }
};

export const StyledButton = styled.button<StyledButtonProps>`
  background-color: ${({
    classType,
    'aria-disabled': ariaDisabled,
    isSubmittingIndicatorActive,
    theme,
  }) =>
    classType === 'PRIMARY'
      ? ariaDisabled && !isSubmittingIndicatorActive
        ? theme.colors.button_disabled
        : theme.colors.button_primary
      : 'transparent'};

  /* no text means this is an icon button */
  padding: ${({ $size, text }) =>
    !text ? '5px 8px;' : $size === 'LARGE' ? '8px 20px;' : '4px 12px;'};

  min-height: ${({ $size }) => ($size === 'LARGE' ? '40px' : '32px')};

  ${({ position }) => (position === 'FIRST' ? 'margin-right: 12px' : '')};

  &:focus {
    outline: 1px solid;
    outline-offset: 3px;
    outline-color: ${({ theme }) => theme.colors.focus};
  }

  transition: background-color 0.15s, border-color 0.15s, opacity 0.15s;

  ${({ 'aria-disabled': ariaDisabled }) => ariaDisabled && 'cursor: not-allowed;'}

  ${(props) => getBorderStyle(props)}

  ${({ isSubmittingIndicatorActive, classType, 'aria-disabled': ariaDisabled, theme }) =>
    !ariaDisabled || isSubmittingIndicatorActive
      ? css`
          &:focus,
          &:hover {
            ${classType === 'PRIMARY'
              ? `background-color: ${theme.colors.button_primary_hover};`
              : ''}
            ${classType === 'SECONDARY'
              ? `box-shadow: inset 0 0 0 1px ${theme.colors.button_secondary_hover};`
              : ''}
          }
        `
      : ''}
`;

export class ButtonPrimaryMedium extends Component<ExternalButtonProps> {
  static displayName = 'ButtonPrimaryMedium';
  render() {
    return (
      <Button data-test="ButtonPrimaryMedium" {...this.props} classType="PRIMARY" size="MEDIUM" />
    );
  }
}

export class ButtonPrimaryLarge extends Component<ExternalButtonProps> {
  static displayName = 'ButtonPrimaryLarge';
  render() {
    return (
      <Button
        data-test="ButtonPrimaryLarge"
        data-testid="ButtonPrimaryLarge"
        {...this.props}
        classType="PRIMARY"
        size="LARGE"
      />
    );
  }
}

export class ButtonSecondaryMedium extends Component<ExternalButtonProps> {
  static displayName = 'ButtonSecondaryMedium';
  render() {
    return (
      <Button
        data-test="ButtonSecondaryMedium"
        {...this.props}
        classType="SECONDARY"
        size="MEDIUM"
      />
    );
  }
}

export class ButtonSecondaryLarge extends Component<ExternalButtonProps> {
  static displayName = 'ButtonSecondaryLarge';
  render() {
    return (
      <Button data-test="ButtonSecondaryLarge" {...this.props} classType="SECONDARY" size="LARGE" />
    );
  }
}

export class ButtonPrimarySmall extends Component<ExternalButtonProps> {
  static displayName = 'ButtonPrimarySmall';
  render() {
    return (
      <Button data-test="ButtonPrimarySmall" {...this.props} classType="PRIMARY" size="SMALL" />
    );
  }
}

export class ButtonSecondarySmall extends Component<ExternalButtonProps> {
  static displayName = 'ButtonSecondarySmall';
  render() {
    return (
      <Button data-test="ButtonSecondarySmall" {...this.props} classType="SECONDARY" size="SMALL" />
    );
  }
}
const styledIconFillColor = (
  disabled: boolean | null,
  color: Color | null,
  theme: DefaultTheme
) => {
  if (disabled) {
    return theme.colors.icon_on_color_disabled;
  } else {
    return color && cssColor(color);
  }
};

export const LinkLikeButton = (
  props: {
    ['data-test']?: string;
    children?: ReactNode;
    color?: Color;
    iconSize?: number;
    title?: string;
  } & ExternalButtonProps
) => {
  const {
    disabled,
    icon,
    iconSize,
    iconOrder,
    onClick,
    skipTracking,
    text,
    tooltip,
    buttonRef: propRef,
    color,
  } = props;
  const innerRef = React.useRef();
  const buttonRef = propRef || innerRef;
  const { getProperties } = useAnalyticsContext();
  const theme = useTheme();
  const fillColor = styledIconFillColor(disabled, color, theme);

  const Comp = (
    <StyledLinkLikeButton
      data-test="LinkLikeButton"
      {...props}
      onClick={(e) =>
        handleButtonClick(e, {
          icon,
          onClick,
          skipTracking,
          text,
          type: 'link',
          analyticsProperties: getProperties(),
        })
      }
      ref={buttonRef}
    >
      <ButtonChildrenWrapper
        hasIconAndText={typeof icon === 'string' && typeof text === 'string' && text.length > 0}
        placeBehind={props.placeBehind}
        reverse={iconOrder === 'last'}
      >
        {typeof icon === 'string' && (
          <StyledIcon
            ariaHidden={Boolean(text)} // if there is text, hide the icon from screen reader
            classType="SECONDARY"
            disabled={disabled}
            fillColor={fillColor}
            isIconButton={!text}
            name={icon}
            size={iconSize || 16}
          />
        )}

        {/* @ts-expect-error - TS2322 - Type '{ children: string; disabled: boolean; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'. */}
        <div disabled={disabled}>{text}</div>
      </ButtonChildrenWrapper>
    </StyledLinkLikeButton>
  );

  // Filter out all falsy values except 0
  if (tooltip || tooltip === 0) {
    return (
      <Tooltip content={tooltip} enterDelayMs={0} ref={buttonRef}>
        {Comp}
      </Tooltip>
    );
  }
  return Comp;
};

export const StyledLinkLikeButton = styled.button<any>`
  font-weight: 400;
  color: ${({ color, disabled, theme }) =>
    disabled === true
      ? theme.colors.button_disabled
      : color
      ? cssColor(color)
      : theme.colors.link_primary};
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  white-space: nowrap;

  &:focus:not(:focus-visible) {
    outline: none;
  }

  ${({ color, theme }) =>
    !color
      ? css`
          &:focus,
          &:hover {
            &:not(:disabled) {
              color: ${theme.colors.link_primary_hover};
            }
          }
        `
      : ''}
`;

export const ClickableLinkStyle = css`
  font-weight: 400;
  text-decoration: underline;
`;

export default Button;
